<template>
    <div>
        <div class="mb-2 flex items-center justify-end">
            <button
                @click="fetchCommissionTypes"
                :disabled="loading"
                class="flex items-center space-x-1 text-xs text-indigo-400 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 transition-colors"
            >
                <ArrowPathIcon :class="{ 'animate-spin': loading }" class="h-3 w-3" />
                <span>{{ loading ? 'Carregando...' : 'Atualizar' }}</span>
            </button>
        </div>
        
        <ticto-loading v-if="loading" />
        
        <div v-else-if="commissionTypes.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
            Nenhum tipo de comissão encontrado
        </div>
        
        <div v-else class="flex flex-col gap-2">
            <div
                v-for="(commission, index) in commissionTypes"
                :key="commission.type"
                class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
                <div class="flex items-center space-x-3">
                    <div class="flex flex-col items-center">
                        <div class="text-xs font-bold text-gray-500 dark:text-gray-400">#{{ index + 1 }}</div>
                        <div class="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold"
                             :class="getCommissionRankColor(index)">
                            {{ index + 1 }}
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <div class="flex items-center space-x-2">
                            <span 
                                class="text-sm font-medium px-2 py-1 rounded"
                                :class="getCommissionTypeColor(commission.type)"
                            >
                                {{ formatCommissionType(commission.type) }}
                            </span>
                            <span v-if="commission.percentage > 70" 
                                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border dark:border-yellow-500/30">
                                <ExclamationTriangleIcon class="h-3 w-3 mr-1" />
                                Concentração Alta
                            </span>
                            <span v-else-if="commission.percentage > 50" 
                                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 dark:border dark:border-blue-500/30">
                                Concentração Média
                            </span>
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ commission.total }} comissões
                        </div>
                    </div>
                </div>
                
                <div class="flex items-center space-x-4">
                    <div class="text-right">
                        <div class="text-xs font-medium" :class="getPercentageColor(commission.percentage)">
                            {{ commission.percentage }}% do total
                        </div>
                    </div>
                    <div class="text-sm font-semibold text-green-600 dark:text-green-400">
                        {{ $root.formatCurrency(commission.amount) }}
                    </div>
                </div>
            </div>
        </div>
        
        <div v-if="commissionTypes.length > 0" class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="text-xs text-gray-500 dark:text-gray-400 text-center">
                Total de {{ commissionTypes.length }} tipos de comissão
            </div>
        </div>
    </div>
</template>

<script>
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import TictoLoading from '../layouts/TictoLoading.vue'
import CommissionType from '../../mixins/CommissionType.vue'

export default {
    components: {
        ArrowPathIcon,
        ExclamationTriangleIcon,
        TictoLoading
    },
    mixins: [CommissionType],
    name: 'UserCommissionTypesCard',
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    
    data() {
        return {
            loading: false,
            commissionTypes: []
        }
    },
    
    mounted() {
        this.$nextTick(() => {
            this.fetchCommissionTypes();
        });
    },
    
    methods: {
        async fetchCommissionTypes() {
            if (!this.user?.id) return;
            
            this.loading = true;
            
            try {
                const { data } = await axios.get(`/api/user/${this.user.id}/commission-types`);
                this.commissionTypes = data.data || [];
            } catch (error) {
                console.error('Erro ao buscar tipos de comissão:', error);
                this.commissionTypes = [];
            } finally {
                this.loading = false;
                this.$emit('data-loaded', this.commissionTypes);
            }
        },
        
        getCommissionRankColor(index) {
            if (index === 0) return 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-300 dark:border dark:border-yellow-500/50'; // 1º lugar - dourado
            if (index === 1) return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300 dark:border dark:border-gray-500/50';     // 2º lugar - prata
            if (index === 2) return 'bg-orange-200 text-orange-800 dark:bg-orange-800/30 dark:text-orange-300 dark:border dark:border-orange-500/50'; // 3º lugar - bronze
            return 'bg-blue-100 text-blue-800 dark:bg-blue-800/30 dark:text-blue-300 dark:border dark:border-blue-500/50';                      // demais
        },
        
        getPercentageColor(percentage) {
            if (percentage > 70) return 'text-red-600 dark:text-red-400';
            if (percentage > 50) return 'text-yellow-600 dark:text-yellow-400';
            return 'text-gray-600 dark:text-gray-400';
        }
    }
}
</script> 